// ===========================================
// SALES BY CATEGORY — retail / e-commerce / food service / distribution
// ===========================================
// The revenue + margin split by each customer's sales category, month over
// month. Everything comes from /wholesale/summary/categories — confirmed,
// fulfilled and invoiced orders only; margin appears once cost-per-case is
// set on Channels. Compact mode renders a small self-hiding dashboard card.
// entity narrows to one set of books (US / EU); Combined converts non-USD
// money at the day's ECB rate, labeled with that date — or shows it per
// currency, unconverted, when no rate is cached yet.

import { useState, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PieChart } from 'lucide-react';
import clsx from 'clsx';
import { wholesaleApi } from '../services/api';
import { formatMoney, formatByCurrency } from '../lib/money';
import type { EntityView } from './EntityToggle';

const dollars = (c: number | null | undefined) => formatMoney(c, 'USD');
const monthLabel = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-US', { month: 'short' });
};
// Calm, distinct fills for the split bar — one per category, grey for "none yet".
const BAR_TONES: Record<string, string> = {
  retail: 'bg-primary-500',
  ecommerce: 'bg-sky-500',
  food_service: 'bg-amber-500',
  distribution: 'bg-emerald-500',
  uncategorized: 'bg-slate-300',
};

export default function CategorySplit({ compact, entity = 'all' }: { compact?: boolean; entity?: EntityView }) {
  const [openCat, setOpenCat] = useState<string | null>(null);
  const { data, isPending } = useQuery({
    queryKey: ['wholesale-category-summary', entity],
    queryFn: async () => (await wholesaleApi.getCategorySummary(entity === 'all' ? undefined : entity)).data?.data || null,
    staleTime: 60_000,
  });
  if (isPending || !data) return compact ? null : <div className="card p-6 text-center text-slate-400">Working out the split…</div>;

  // A row counts when any money moved — converted USD when a rate exists,
  // native per-currency subtotals when it doesn't (never silently dropped).
  const hasMoney = (r: any) => (r.revenueCents ?? 0) > 0 || Object.values(r.revenueByCurrency || {}).some((v: any) => Number(v) > 0);
  const rows: any[] = (data.rows || []).filter(hasMoney);
  const totalRev = rows.reduce((s, r) => s + (r.revenueCents ?? 0), 0);
  // Revenue cell: USD equivalent (tooltip carries the native breakdown), or the
  // native subtotals themselves when no rate is cached yet.
  const revenueCell = (r: any) =>
    r.revenueCents == null
      ? <span title="No exchange rate cached yet — shown per currency, unconverted">{formatByCurrency(r.revenueByCurrency)}</span>
      : <span title={r.revenueLabel || undefined}>{dollars(r.revenueCents)}</span>;
  const categorized = rows.filter((r) => r.category !== 'uncategorized');

  // Month over month: the two most recent months with data, per row.
  const months: string[] = data.months || [];
  const mom = (r: any) => {
    const withData = (r.monthly || []).filter((m: any) => m.revenueCents > 0);
    if (withData.length < 2) return null;
    const cur = withData[withData.length - 1];
    const prev = withData[withData.length - 2];
    if (!prev.revenueCents) return null;
    return { month: cur.month, pct: Math.round(((cur.revenueCents - prev.revenueCents) / prev.revenueCents) * 100) };
  };

  // Compact card for the dashboard home — hides until a categorized dollar exists.
  if (compact) {
    if (categorized.length === 0) return null;
    return (
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-3">
          <PieChart className="w-4 h-4 text-primary-600" />
          <h2 className="font-semibold text-slate-900">Revenue by category</h2>
        </div>
        <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100 mb-3">
          {rows.map((r) => (
            <div key={r.category} className={BAR_TONES[r.category] || 'bg-slate-300'}
              style={{ width: `${Math.max(2, ((r.revenueCents ?? 0) / (totalRev || 1)) * 100)}%` }}
              title={`${r.label}: ${dollars(r.revenueCents)}`} />
          ))}
        </div>
        <div className="space-y-1.5">
          {rows.map((r) => {
            const d = mom(r);
            return (
              <div key={r.category} className="flex items-baseline justify-between text-sm gap-2">
                <span className="flex items-center gap-1.5 text-slate-600 min-w-0">
                  <span className={clsx('w-2 h-2 rounded-full shrink-0', BAR_TONES[r.category] || 'bg-slate-300')} />
                  <span className="truncate">{r.label}</span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="font-semibold text-slate-900">{revenueCell(r)}</span>
                  <span className="text-xs text-slate-400"> · {totalRev ? Math.round(((r.revenueCents ?? 0) / totalRev) * 100) : 0}%</span>
                  {d && <span className={clsx('text-xs ml-1.5', d.pct >= 0 ? 'text-emerald-600' : 'text-rose-600')}>{d.pct >= 0 ? '+' : ''}{d.pct}% MoM</span>}
                </span>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-400 mt-3">From confirmed wholesale orders, split by each customer's sales category — set it on Wholesale → Customers.</p>
      </div>
    );
  }

  // Full view — the Channels tab section.
  if (rows.length === 0) {
    return (
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-1">
          <PieChart className="w-4 h-4 text-primary-600" />
          <h2 className="font-semibold text-slate-900">Sales by category</h2>
        </div>
        <p className="text-sm text-slate-500">No counted orders yet — once orders confirm, revenue splits here by each customer's sales category (set it on the Customers tab).</p>
      </div>
    );
  }
  return (
    <div className="card p-0 overflow-hidden">
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center gap-2">
          <PieChart className="w-4 h-4 text-primary-600" />
          <h2 className="font-semibold text-slate-900">Sales by category</h2>
        </div>
        <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100 mt-3">
          {rows.map((r) => (
            <div key={r.category} className={BAR_TONES[r.category] || 'bg-slate-300'}
              style={{ width: `${Math.max(2, ((r.revenueCents ?? 0) / (totalRev || 1)) * 100)}%` }}
              title={`${r.label}: ${dollars(r.revenueCents)}`} />
          ))}
        </div>
        {data.fx?.label && <p className="text-[11px] text-slate-400 mt-2">{data.fx.label}</p>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
              <th className="px-4 py-2 font-medium">Category</th>
              <th className="px-4 py-2 font-medium text-right">Accounts</th>
              <th className="px-4 py-2 font-medium text-right">Revenue</th>
              <th className="px-4 py-2 font-medium text-right">Share</th>
              {data.hasCosts && <th className="px-4 py-2 font-medium text-right">Margin</th>}
              <th className="px-4 py-2 font-medium text-right">Vs last month</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {rows.map((r) => {
              const d = mom(r);
              const isOpen = openCat === r.category;
              const colCount = 5 + (data.hasCosts ? 1 : 0);
              return (
                <Fragment key={r.category}>
                  <tr onClick={() => setOpenCat(isOpen ? null : r.category)} className="cursor-pointer hover:bg-slate-50/60" title="Click for month-by-month">
                    <td className="px-4 py-2.5 font-medium text-slate-900">
                      <span className="flex items-center gap-2">
                        <span className={clsx('w-2 h-2 rounded-full shrink-0', BAR_TONES[r.category] || 'bg-slate-300')} />
                        {r.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{r.customers}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-slate-900">{revenueCell(r)}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{totalRev ? Math.round(((r.revenueCents ?? 0) / totalRev) * 100) : 0}%</td>
                    {data.hasCosts && (
                      <td className="px-4 py-2.5 text-right">
                        {r.marginCents == null ? <span className="text-slate-300">—</span> : (
                          <><span className="font-medium text-slate-900">{dollars(r.marginCents)}</span>{' '}
                          <span className={clsx('text-xs', (r.marginPct ?? 0) >= 30 ? 'text-emerald-600' : (r.marginPct ?? 0) >= 15 ? 'text-amber-600' : 'text-rose-600')}>({r.marginPct}%)</span></>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-2.5 text-right">
                      {d ? <span className={clsx('text-xs', d.pct >= 0 ? 'text-emerald-600' : 'text-rose-600')}>{d.pct >= 0 ? '+' : ''}{d.pct}%</span> : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={colCount} className="px-4 pb-3 pt-1 bg-slate-50/50">
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-1.5">
                          {months.map((m, i) => {
                            const cell = r.monthly?.[i];
                            return (
                              <div key={m} className="rounded-lg bg-white border border-slate-100 px-1.5 py-1 text-center">
                                <p className="text-[10px] text-slate-400">{monthLabel(m)}</p>
                                <p className="text-xs font-semibold text-slate-900">{cell?.revenueCents ? dollars(cell.revenueCents) : '—'}</p>
                                {data.hasCosts && cell?.revenueCents > 0 && cell?.marginCents != null && (
                                  <p className="text-[10px] text-slate-400">{dollars(cell.marginCents)} mgn</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[11px] text-slate-400 mt-1.5">Revenue each month{data.hasCosts ? ' with the margin underneath' : ''}.</p>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 px-4 py-2.5 border-t border-slate-50">{data.source}</p>
    </div>
  );
}
